/**
 * A playlist's artwork.
 *
 * There is no such thing as a playlist cover in the domain model, and inventing one
 * would mean either a second artwork upload flow or a fabricated image. So a playlist
 * wears its first track's cover, which is what it actually contains — and because
 * SongArtwork is the one renderer for song artwork, applying a new cover to that
 * track updates the playlist too, with no synchronisation anywhere.
 *
 * An empty playlist gets a dashed box with the list glyph, matching the ACTIVE SONG
 * panel's "no cover art yet" treatment: dashed means "nothing here yet", solid means
 * "this is the artwork".
 */
import type { CSSProperties } from 'react';
import { ListMusic } from 'lucide-react';
import { SongArtwork } from '../songs';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface PlaylistArtworkProps {
  /** The playlist's songs, in order. Only the first is drawn. */
  songs: Song[];
  size: number;
  radius?: number;
  /** Glyph size for the empty state. Scales with `size` when omitted. */
  iconSize?: number;
  style?: CSSProperties;
}

export default function PlaylistArtwork({
  songs,
  size,
  radius = 8,
  iconSize,
  style,
}: PlaylistArtworkProps) {
  const first = songs[0];

  if (first) {
    return (
      <SongArtwork
        song={first}
        height={size}
        radius={radius}
        style={{ width: size, flexShrink: 0, overflow: 'hidden', ...style }}
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        flexShrink: 0,
        background: c.surface2,
        border: `1px dashed ${c.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <ListMusic size={iconSize ?? Math.round(size * 0.36)} color={c.text3} />
    </div>
  );
}
